// src/components/BestPickerModal.jsx
// Picker modal for selecting a best place from your saved places
import { useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

const FH = "'Noto Serif', Georgia, serif";
const FL = "'Manrope', -apple-system, sans-serif";
const C = {
  primary: "#655d54", primaryContainer: "#ede0d5",
  bg: "#faf9f6", surfaceLow: "#f4f4f0", container: "#edeeea",
  onSurface: "#2f3430", onSurfaceVariant: "#5c605c",
  outlineVariant: "#8a8e8a",
};

export default function BestPickerModal({ category, categoryLabel, personalPlaces, myBestPicks, onSelect, onClose }) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const mobile = window.innerWidth <= 768;

  // Collect IDs of places already added to Best
  const pickedIds = new Set();
  const pickedGoogleIds = new Set();
  Object.values(myBestPicks).flat().forEach((p) => {
    if (p.personal_place_id) pickedIds.add(p.personal_place_id);
    if (p.google_place_id) pickedGoogleIds.add(p.google_place_id);
  });

  const filtered = personalPlaces
    .filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));

  const isPicked = (p) => pickedIds.has(p.id) || (p.google_place_id && pickedGoogleIds.has(p.google_place_id));

  return createPortal(
    <div
      onClick={(e) => e.target === e.currentTarget && onClose()}
      style={{
        position: "fixed", inset: 0, zIndex: 100,
        display: "flex", alignItems: mobile ? "flex-end" : "center", justifyContent: "center",
        background: "rgba(47,52,48,0.4)", backdropFilter: "blur(4px)",
      }}
    >
      <div style={{
        width: mobile ? "100%" : 400,
        maxHeight: mobile ? "85vh" : "70vh",
        background: C.bg,
        borderRadius: mobile ? "18px 18px 0 0" : 16,
        display: "flex", flexDirection: "column",
        boxShadow: "0 8px 40px rgba(0,0,0,0.15)",
        overflow: "hidden",
      }}>
        {/* Header */}
        <div style={{
          padding: "16px 20px 12px",
          borderBottom: `1px solid ${C.container}`,
          display: "flex", alignItems: "center", justifyContent: "space-between",
        }}>
          <div>
            <p style={{ margin: 0, fontFamily: FL, fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.15em", color: C.outlineVariant }}>
              {t("best.add_to_best")}
            </p>
            <h3 style={{ margin: "2px 0 0", fontFamily: FH, fontSize: 16, fontWeight: 400, color: C.onSurface }}>
              {categoryLabel}
            </h3>
          </div>
          <button onClick={onClose} style={{
            width: 28, height: 28, background: C.surfaceLow, border: "none",
            borderRadius: "50%", cursor: "pointer", display: "flex",
            alignItems: "center", justifyContent: "center",
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 16, color: C.onSurfaceVariant }}>close</span>
          </button>
        </div>

        {/* Search */}
        <div style={{ padding: "10px 20px" }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t("best.search_places")}
            style={{
              width: "100%", padding: "8px 12px",
              background: C.surfaceLow, border: "none", borderRadius: 8,
              fontFamily: FL, fontSize: 13, color: C.onSurface,
              outline: "none", boxSizing: "border-box",
            }}
          />
        </div>

        {/* Place list */}
        <div style={{ flex: 1, overflowY: "auto", padding: "0 12px 16px" }}>
          {filtered.length === 0 && personalPlaces.length === 0 && (
            <div style={{ textAlign: "center", padding: "32px 20px" }}>
              <span className="material-symbols-outlined" style={{ fontSize: 36, color: C.container, marginBottom: 8, display: "block" }}>add_location_alt</span>
              <p style={{ fontFamily: FL, fontSize: 14, fontWeight: 600, color: C.onSurfaceVariant, margin: "0 0 6px" }}>
                {t("best.no_places")}
              </p>
              <p style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant, margin: 0, lineHeight: 1.5 }}>
                {t("best.no_places_desc")}
              </p>
            </div>
          )}
          {filtered.length === 0 && personalPlaces.length > 0 && (
            <p style={{ fontFamily: FL, fontSize: 12, color: C.outlineVariant, textAlign: "center", padding: 20, fontStyle: "italic" }}>
              {t("best.no_results")}
            </p>
          )}
          {filtered.map((p) => {
            const picked = isPicked(p);
            return (
              <button
                key={p.id}
                disabled={picked}
                onClick={() => {
                  const hasPhoto = p.photo_urls?.length > 0 || !!p.photo_url;
                  if (!hasPhoto) { alert(t("best.photo_required")); return; }
                  onSelect({
                    category,
                    name: p.name, address: p.address,
                    lat: p.lat, lng: p.lng,
                    google_place_id: p.google_place_id,
                    google_maps_url: p.google_maps_url,
                    personal_place_id: p.id,
                  });
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%",
                  padding: "10px 12px", border: "none", borderRadius: 8,
                  background: "transparent", cursor: picked ? "default" : "pointer",
                  opacity: picked ? 0.45 : 1,
                  textAlign: "left", transition: "background 0.12s",
                }}
                onMouseEnter={(e) => { if (!picked) e.currentTarget.style.background = C.surfaceLow; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; }}
              >
                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{
                    margin: 0, fontFamily: FL, fontSize: 13, fontWeight: 600, color: C.onSurface,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                  }}>{p.name}</p>
                  {p.address && (
                    <p style={{
                      margin: "2px 0 0", fontFamily: FL, fontSize: 10, color: C.outlineVariant,
                      overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                    }}>{p.address}</p>
                  )}
                </div>
                {picked && (
                  <span style={{
                    fontFamily: FL, fontSize: 9, fontWeight: 700, color: C.primary,
                    background: C.primaryContainer, padding: "2px 8px", borderRadius: 4,
                    flexShrink: 0,
                  }}>{t("best.added")}</span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body,
  );
}
